import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { clientContractsService } from '@/features/client-contracts/services/client-contracts.service';
import { ClientContractSettingsForm } from '@/features/client-contracts/components/ClientContractSettingsForm';
import { teamsService } from '@/features/teams/services/teams.service';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/client-contracts/settings')({
  component: ClientContractSettingsPage,
});

function ClientContractSettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({
    queryKey: ['client-contract-settings'],
    queryFn: () => clientContractsService.getSettings(),
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsService.getAll().then((res) => res.data || []),
  });

  const updateMutation = useMutation({
    mutationFn: clientContractsService.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-contract-settings'] });
      toast({ title: 'Configurações atualizadas' });
    },
    onError: () => {
      toast({
        title: 'Erro ao salvar configurações',
        variant: 'destructive',
      });
    },
  });

  if (!settings) {
    return (
      <div className="text-muted-foreground">
        Carregando configurações...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/client-contracts" search={{ clientId: undefined }}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            Configurações de Contratos de Clientes
          </h1>
          <p className="text-muted-foreground">
            Defina prazos e equipe responsável por notificações de vencimento.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Notificações</CardTitle>
        </CardHeader>
        <CardContent>
          <ClientContractSettingsForm
            settings={settings}
            teams={teams}
            saving={updateMutation.isPending}
            onSave={(data) => updateMutation.mutate(data)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
